import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import { formatCurrency } from '../../../utils/currency';

const JournalEntryTable = ({
  entries,
  selectedEntries,
  expandedEntries,
  onSelectEntry,
  onSelectAll,
  onToggleExpand,
  sortConfig,
  onSort,
  onEdit,
  onDelete,
  onReverse,
  onViewDetails
}) => {

  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc'
      ? <Icon name="ChevronUp" size={16} className="text-primary" />
      : <Icon name="ChevronDown" size={16} className="text-primary" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Posted': { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle' },
      'Draft': { bg: 'bg-secondary/10', text: 'text-secondary', icon: 'Edit' },
      'Pending Approval': { bg: 'bg-warning/10', text: 'text-warning', icon: 'Clock' },
      'Reversed': { bg: 'bg-error/10', text: 'text-error', icon: 'RotateCcw' }
    };

    const config = statusConfig?.[status] || statusConfig?.['Draft'];

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        {status}
      </span>
    );
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <Checkbox
                  checked={selectedEntries?.length === entries?.length && entries?.length > 0}
                  onChange={onSelectAll}
                  indeterminate={selectedEntries?.length > 0 && selectedEntries?.length < entries?.length}
                />
              </th>
              <th className="px-4 py-3 text-left w-12"></th>
              <th
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('entryNumber')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Entry #</span>
                  {getSortIcon('entryNumber')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('entryDate')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Date</span>
                  {getSortIcon('entryDate')}
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="font-heading font-semibold text-sm text-foreground">Description</div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="font-heading font-semibold text-sm text-foreground">Reference</div>
              </th>
              <th className="px-4 py-3 text-right">
                <div className="font-heading font-semibold text-sm text-foreground">Amount</div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="font-heading font-semibold text-sm text-foreground">Status</div>
              </th>
              <th className="px-4 py-3 text-right w-24">
                <div className="font-heading font-semibold text-sm text-foreground">Actions</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {entries?.map((entry) => (
              <React.Fragment key={entry?.id}>
                <tr className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedEntries?.includes(entry?.id)}
                      onChange={() => onSelectEntry(entry?.id)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onToggleExpand(entry?.id)}
                      className="p-1 hover:bg-muted rounded transition-smooth"
                    >
                      <Icon
                        name={expandedEntries?.includes(entry?.id) ? 'ChevronDown' : 'ChevronRight'}
                        size={16}
                        className="text-muted-foreground"
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground">{entry?.entryNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">{formatDate(entry?.entryDate)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">{entry?.description}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">{entry?.reference}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium text-foreground">{formatCurrency(entry?.totalDebit)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(entry?.status)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(entry)}
                        title="Edit"
                      >
                        <Icon name="Edit" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onReverse(entry?.id)}
                        title="Reverse Entry"
                      >
                        <Icon name="RotateCcw" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(entry)}
                        title="Delete"
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </Button>
                    </div>
                  </td>
                </tr>
                {expandedEntries?.includes(entry?.id) && (
                  <tr className="bg-muted/20">
                    <td colSpan="9" className="px-4 py-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-heading font-semibold text-sm text-foreground">Line Items</h4>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>Created by: {entry?.createdBy}</span>
                            {entry?.approvedBy && <span>Approved by: {entry?.approvedBy}</span>}
                          </div>
                        </div>
                        <div className="bg-card rounded-lg border border-border overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Account</th>
                                <th className="px-4 py-2 text-right text-xs font-semibold text-foreground">Debit</th>
                                <th className="px-4 py-2 text-right text-xs font-semibold text-foreground">Credit</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-foreground">Memo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry?.lineItems?.map((line, index) => (
                                <tr key={index} className="border-t border-border">
                                  <td className="px-4 py-2 text-sm text-foreground">{line?.account}</td>
                                  <td className="px-4 py-2 text-right text-sm font-medium text-foreground">
                                    {line?.debit > 0 ? formatCurrency(line?.debit) : '-'}
                                  </td>
                                  <td className="px-4 py-2 text-right text-sm font-medium text-foreground">
                                    {line?.credit > 0 ? formatCurrency(line?.credit) : '-'}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-muted-foreground">{line?.memo}</td>
                                </tr>
                              ))}
                              <tr className="border-t-2 border-border bg-muted/30">
                                <td className="px-4 py-2 text-sm font-semibold text-foreground">Total</td>
                                <td className="px-4 py-2 text-right text-sm font-bold text-foreground">
                                  {formatCurrency(entry?.totalDebit)}
                                </td>
                                <td className="px-4 py-2 text-right text-sm font-bold text-foreground">
                                  {formatCurrency(entry?.totalCredit)}
                                </td>
                                <td className="px-4 py-2"></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JournalEntryTable;